import { FC, useState } from 'react'

import {
  Button,
  TextAreaFieldInput,
  useNotifications,
  Flex
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { ActivityLogItemTemplate } from '../ActivityLogItemTemplate'
import { ActivityEntityType } from '../types'
import { NoteIcon } from './NoteIcon'

export const ActivityLogNoteForm: FC<{
  entityId: string
  entityType: ActivityEntityType
  isLast: boolean
  onDismiss: () => void
}> = ({ entityId, entityType, isLast, onDismiss }) => {
  const { displayNotification } = useNotifications()
  const [summary, setSummary] = useState<string>('')

  const noteMutation = useMutation(
    dashboardv99990101Client.postActivityLogNote,
    {
      onSuccess: () => {
        displayNotification('Note added to activity log', { type: 'success' })
        onDismiss()
      },
      onError: () => {
        displayNotification('Unable to save note', { type: 'error' })
      }
    }
  )

  const onSubmit = async () => {
    await noteMutation.mutateAsync({
      activityLogObjectEntityId: entityId,
      body: { summary, activityLogObjectEntityType: entityType }
    })
  }

  return (
    <ActivityLogItemTemplate
      icon={<NoteIcon />}
      isLast={isLast}
      data-testid="activity-log.inspector.add-note.container"
    >
      <Flex flexDir="column" gap="8px" flex="1" marginBottom="12px">
        <TextAreaFieldInput
          autoFocus
          value={summary}
          onChange={e => setSummary(e.target.value)}
          maxLength={1000}
          placeholder="Add a note..."
          height="64px"
          style={{ minHeight: 'initial' }}
          isDisabled={noteMutation.isPending}
          data-testid="activity-log.inspector.add-note.field"
        />

        <Flex gap="8px" justifyContent="flex-end">
          <Button
            variant="secondary"
            disabled={noteMutation.isPending}
            onClick={onDismiss}
            data-testid="activity-log.inspector.add-note.cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            keyboardShortcut={['Mod', 'Enter']}
            hideKeyboardShortcut
            disabled={summary.length === 0 || noteMutation.isPending}
            data-testid="activity-log.inspector.add-note.submit"
          >
            Add note
          </Button>
        </Flex>
      </Flex>
    </ActivityLogItemTemplate>
  )
}
